import {
	ColumnDef,
	getCoreRowModel,
	SortingState,
	useReactTable,
	getSortedRowModel,
	flexRender,
} from '@tanstack/react-table';

import { memo, useRef, useState } from 'react';
import styled from 'styled-components';
import { InvestorFileMapping } from '@dealstack/frontend/admin/pages/Manage/MIP/ManageMip/SimpleDocumentWorkflowBatch/types';
import { rem } from '@mantine/core';

interface Props {
	data: InvestorFileMapping[];
	onClickRow?: (info: InvestorFileMapping) => void;
}

const InvestorWithFileTable = ({ data, onClickRow }: Props) => {
	const tableContainerRef = useRef<HTMLTableElement>(null);

	const [sorting] = useState<SortingState>([
		{
			desc: false,
			id: 'firstName',
		},
	]);

	const manageListInvestorsColumns = [
		{
			id: 'investorId',
			accessorFn: (row: InvestorFileMapping) => row?.investor?.id,
			enableSorting: true,
			size: 10,
			header: 'Id',
			cell: (info) => info.row.original?.investor?.id,
		},
		{
			id: 'firstName',
			accessorFn: (row: InvestorFileMapping) => row?.investor?.user?.firstName,
			enableSorting: true,
			size: 10,
			header: 'First name',
			cell: (info) => info.row.original?.investor?.user?.firstName,
		},
		{
			id: 'lastName',
			accessorFn: (row: InvestorFileMapping) => row?.investor?.user?.lastName,
			enableSorting: true,
			size: 10,
			header: 'Last name',
			cell: (info) => info.row.original?.investor?.user?.lastName,
		},
		{
			id: 'fileName',
			accessorFn: (row: InvestorFileMapping) => row?.file?.name,
			enableSorting: true,
			size: 40,
			header: 'File Name',
			cell: (info) => info.row.original?.file?.name,
		},
	] as ColumnDef<InvestorFileMapping>[];

	const tanstackTable = useReactTable({
		data,
		columns: manageListInvestorsColumns,
		getCoreRowModel: getCoreRowModel(),
		getSortedRowModel: getSortedRowModel(),
		state: {
			sorting,
		},
	});

	return (
		<div style={{ position: 'relative' }}>
			<TableWrapper>
				<table className="table" ref={tableContainerRef}>
					<thead>
						{tanstackTable.getHeaderGroups().map((headerGroup) => (
							<tr key={headerGroup.id}>
								{headerGroup.headers.map((header) => {
									return (
										<th
											key={header.id}
											colSpan={header.colSpan}
											style={{ width: header.getSize() }}
										>
											{header.isPlaceholder ? null : (
												<div
													{...{
														className: header.column.getCanSort()
															? 'cursor-pointer clickAnimation select-none'
															: '',
														onClick:
															header.column.getToggleSortingHandler(),
													}}
												>
													{flexRender(
														header.column.columnDef.header,
														header.getContext()
													)}
													{{
														asc: ' 🔼',
														desc: ' 🔽',
													}[header.column.getIsSorted() as string] ??
														null}
												</div>
											)}
										</th>
									);
								})}
							</tr>
						))}
					</thead>
					<tbody>
						{tanstackTable.getRowModel().rows.map((row) => {
							return (
								<tr key={row.id} onClick={() => onClickRow?.(row.original)}>
									{row.getVisibleCells().map((cell) => {
										return (
											<td key={cell.id}>
												{flexRender(
													cell.column.columnDef.cell,
													cell.getContext()
												)}
											</td>
										);
									})}
								</tr>
							);
						})}
					</tbody>
				</table>
			</TableWrapper>
		</div>
	);
};

const TableWrapper = styled.div`
	border: 1px solid lightgray;
	overflow: auto;
	max-height: 40rem;
	position: relative;

	table {
		border-collapse: collapse;
		border-spacing: 0;
		font-family: arial, sans-serif;
		table-layout: fixed;
		width: 100%;
	}

	thead {
		background: white !important;
		margin: 0;
		position: sticky;
		top: 0;
		opacity: 1;
		z-index: 1;
	}

	th {
		border-bottom: 1px solid lightgray;
		border-right: 1px solid lightgray;
		padding: ${rem(2)} ${rem(4)};
		text-align: left;
	}

	td {
		padding: ${rem(10)} ${rem(6)};
	}

	button {
		:focus {
			border: none;
		}
	}
`;
export const investorWithFileTable = memo(InvestorWithFileTable);
